import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Clock, Code, Lightbulb, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const Prijzen = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-noordblue mb-4">Onze tarieven</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Heldere tarieven zonder verrassingen. Kies het pakket dat bij jouw wensen past.
              Alle prijzen zijn exclusief btw.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Workshop/Presentatie */}
          <Card className="flex flex-col border-2 border-noordblue/10 hover:border-noordblue/30 transition-all duration-300">
            <CardHeader>
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-noordblue/10 text-noordblue mb-4">
                <Lightbulb className="w-6 h-6" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-800">Workshop</CardTitle>
              <CardDescription>Perfect voor teams die snel aan de slag willen</CardDescription>
              <div className="mt-4">
                <span className="text-4xl font-bold text-noordblue">€999</span>
                <span className="text-gray-500"> / sessie</span>
              </div>
            </CardHeader>
            <CardContent className="flex-grow">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>3 uur interactieve workshop of presentatie</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Maximaal 15 deelnemers</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Inclusief voorbereiding en materiaal</span>
                </li>
              </ul>
            </CardContent>
            <CardFooter>
              <Button className="w-full bg-noordblue hover:bg-noordblue-dark" asChild>
                <Link to="/contact">Boek nu</Link>
              </Button>
            </CardFooter>
          </Card>

          {/* 8 uur development */}
          <Card className="flex flex-col border-2 border-noordblue shadow-lg transform hover:scale-[1.02] transition-all duration-300">
            <div className="absolute -top-3 right-4 bg-noordblue text-white text-xs font-semibold px-3 py-1 rounded-full">
              Populair
            </div>
            <CardHeader>
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-noordblue text-white mb-4">
                <Code className="w-6 h-6" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-800">Development</CardTitle>
              <CardDescription>Ideal voor projecten met duidelijke scope</CardDescription>
              <div className="mt-4">
                <span className="text-4xl font-bold text-noordblue">€720</span>
                <span className="text-gray-500"> / 8 uur</span>
              </div>
              <div className="text-sm text-gray-500">daarna €90/uur</div>
            </CardHeader>
            <CardContent className="flex-grow">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>8 uur development tijd</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Flexibele inzet</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Transparante communicatie</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Code review en documentatie</span>
                </li>
              </ul>
            </CardContent>
            <CardFooter>
              <Button className="w-full bg-noordblue hover:bg-noordblue-dark" asChild>
                <Link to="/contact">Start project</Link>
              </Button>
            </CardFooter>
          </Card>

          {/* 1 uur development */}
          <Card className="flex flex-col border-2 border-noordblue/10 hover:border-noordblue/30 transition-all duration-300">
            <CardHeader>
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-noordblue/10 text-noordblue mb-4">
                <Clock className="w-6 h-6" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-800">Uurtje flex</CardTitle>
              <CardDescription>Voor kleine vragen en aanpassingen</CardDescription>
              <div className="mt-4">
                <span className="text-4xl font-bold text-noordblue">€110</span>
                <span className="text-gray-500"> / uur</span>
              </div>
            </CardHeader>
            <CardContent className="flex-grow">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>1 uur flexibele inzet</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Kleine aanpassingen of advies</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Geen langdurige verplichtingen</span>
                </li>
              </ul>
            </CardContent>
            <CardFooter>
              <Button className="w-full bg-noordblue hover:bg-noordblue-dark" asChild>
                <Link to="/contact">Vraag aan</Link>
              </Button>
            </CardFooter>
          </Card>
          </div>

          <div className="mt-16 bg-white rounded-xl p-8 shadow-sm max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-1 bg-noordblue mx-auto mb-6"></div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Meer informatie nodig?</h2>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Twijfel je welk pakket het beste bij je past? Of heb je een specifieke vraag over onze diensten?
                Neem gerust contact met ons op voor een vrijblijvend adviesgesprek.
              </p>
              <Button className="bg-noordblue hover:bg-noordblue-dark" size="lg" asChild>
                <Link to="/contact">Neem contact op</Link>
              </Button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Prijzen;
